/**
 * DM Curated First — Debutify 8.x
 *
 * Pins one product to position #1 of a collection grid from a URL parameter, so
 * a Meta/Facebook catalog ad can land the shopper on a collection with the exact
 * design they clicked already first.
 *
 * Ported from BuildMyPOD's Horizon 4.1.x implementation. The algorithm is the
 * same; the DOM contract is completely different and was re-derived against
 * Debutify 8.4 source, not guessed.
 *
 * ---------------------------------------------------------------------------
 * DOM CONTRACT — verified against Debutify 8.4
 *   sections/main-collection-product-grid.liquid
 *   snippets/card-product.liquid
 *   assets/facets.js, assets/dbtfy-infinite-scroll.js, assets/animations.js
 *
 *   <div id="ProductGridContainer">          <-- PERSISTS across filter renders
 *     <div class="collection">
 *       <ul id="product-grid" class="grid product-grid">   <-- REPLACED on filter
 *         <li class="grid__item [scroll-trigger animate--slide-in]">
 *           <div class="card-wrapper product-card-wrapper">
 *             <div class="card card--card card--media ...">
 *               <div class="card__inner ratio image--ratio">
 *                 <product-card-slider>
 *                   <div class="card__media">
 *                     <div class="media media--transparent media-{ratio}">
 *                       <img loading="lazy" ...>
 *               <div class="card__content">
 *                 <h3 class="card__heading">
 *                   <a class="full-unstyled-link" href="{{ product.url }}">
 *
 * WHY THE OBSERVER SITS ON #ProductGridContainer, NOT #product-grid:
 *   facets.js renderProductGridContainer() does
 *     document.getElementById('ProductGridContainer').innerHTML = <parsed>
 *   so on every filter or sort the <ul id="product-grid"> is DESTROYED and a
 *   brand-new node takes its place. A grid-scoped observer would detach itself
 *   on the first filter and never fire again. #ProductGridContainer is the
 *   element whose innerHTML is assigned, so it survives — observe that.
 *   Every grid lookup below re-queries; nothing caches the <ul>.
 *
 * ONE GOVERNING ANCHOR (unlike Horizon):
 *   Debutify inherits Dawn's overlay pattern —
 *     .card__heading a:after { content:""; position:absolute; inset:0; z-index:1 }
 *   so the heading anchor's pseudo-element covers the whole card and one href
 *   controls the entire card click, image included. We still walk every
 *   a[href*="/products/"] and filter by handle, because quick-add, swatches and
 *   third-party apps add their own anchors, and matching by handle leaves
 *   cross-sell links to OTHER products untouched.
 *
 * NO data-page LAYER:
 *   Horizon's paginated-list.js derives the previous page from
 *   cards[0].dataset.page, so BMP had to stamp it on every pin. Debutify's
 *   dbtfy-infinite-scroll.js is forward-only — it reads a URL off its own
 *   button and does container.insertAdjacentHTML('beforeend', ...). Nothing
 *   reads grid position. That whole layer is dropped here.
 *   Duplicate detection is still required: appending page 2 can legitimately
 *   bring in the genuine server-rendered card for the pinned product.
 *
 * SCROLL ANIMATIONS ARE A TRAP — see neutralizeScrollAnimation().
 *
 * Config arrives on the loader <script> tag's data-* attributes.
 * Nothing is written to window. The only global read is Shopify.routes.root.
 */

const cfg = readConfig();

if (cfg.enabled) {
  const params = new URLSearchParams(window.location.search);
  const firstParam = params.get(cfg.param);

  if (firstParam) {
    const parsed = parseFirstParam(firstParam, params.get('variant'));
    if (parsed.handle) init(parsed, cfg);
  }
}

// ---- config ---------------------------------------------------------------

function readConfig() {
  const el = document.querySelector('script[data-dm-curated-first]');
  const d = (el && el.dataset) || {};
  return {
    enabled: el != null,
    param: d.param || 'first',
    swapImage: d.swapImage !== 'false',
    cardSection: d.cardSection || 'dm-curated-card',
    debug: d.debug === 'true',
  };
}

function log() {
  if (!cfg.debug || !window.console) return;
  console.log.apply(console, ['[dm-curated-first]'].concat(Array.prototype.slice.call(arguments)));
}

// ---- storefront routing ---------------------------------------------------

/** Locale-root prefix (e.g. "/en-ca/"), always trailing-slashed. */
function shopifyRoot() {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return root.charAt(root.length - 1) === '/' ? root : root + '/';
}

// ---- param parsing --------------------------------------------------------

/**
 * `first` may be a bare handle or a full/encoded product URL — Meta's catalog
 * macro emits the latter. Also pulls `variant` out of the encoded URL when it
 * was not passed separately.
 *
 * URLSearchParams#get() already percent-decodes once, so the value is normally
 * plain text here. Some ad-link generators double-encode, so we decode ONE more
 * time — but ONLY when the value does not already read as a URL, and only act
 * on the result if that decode reveals one. Unconditional decoding throws on a
 * handle containing a literal "%" (e.g. "50%-off-tee"), hence the try/catch.
 */
function parseFirstParam(firstParam, fallbackVariant) {
  let handle = firstParam;
  let variant = fallbackVariant || null;
  let candidate = firstParam;

  if (!/^https?:\/\//.test(candidate)) {
    try {
      const maybeDecoded = decodeURIComponent(candidate);
      if (/^https?:\/\//.test(maybeDecoded)) candidate = maybeDecoded;
    } catch (e) {
      /* not valid percent-encoding — fall through to the plain-handle path */
    }
  }

  if (/^https?:\/\//.test(candidate)) {
    try {
      const url = new URL(candidate);
      const parts = url.pathname.split('/');
      const idx = parts.indexOf('products');
      handle = idx !== -1 && parts[idx + 1] ? parts[idx + 1] : '';
      if (!variant) variant = new URLSearchParams(url.search).get('variant');
    } catch (e) {
      /* malformed URL — treat firstParam as a plain handle */
    }
  }

  handle = (handle || '').split('?')[0].split('#')[0].split('/').pop() || '';
  // Handles are lowercase alphanumerics and hyphens. Anything else is either a
  // bad link or an injection attempt; both should resolve to "no pin".
  if (!/^[a-z0-9-]+$/.test(handle)) handle = '';
  // Variant ids are always integers.
  variant = (variant || '').replace(/[^0-9]/g, '') || null;

  return { handle, variant };
}

// ---- grid helpers ---------------------------------------------------------

function getContainer() {
  return document.getElementById('ProductGridContainer');
}

/**
 * The grid <ul>, re-queried every time.
 *
 * Requires an actual UL. Debutify's zero-results state renders
 * `<div class="collection collection--empty" id="product-grid">` — SAME id, not
 * a list. Injecting an <li> there would produce an orphaned list item inside a
 * centred empty-state message. Returning null means an empty collection simply
 * does not get a pin, which is the correct outcome: there is no grid to pin to.
 */
function getGrid() {
  const container = getContainer();
  if (!container) return null;
  const grid = container.querySelector('ul#product-grid, ul.product-grid');
  return grid || null;
}

function getCards(grid) {
  return Array.prototype.slice.call(grid.querySelectorAll(':scope > li.grid__item'));
}

function firstCardOf(grid) {
  return grid.querySelector(':scope > li.grid__item');
}

function cardMatchesHandle(card, handle) {
  const links = card.querySelectorAll('a[href*="/products/"]');
  for (let i = 0; i < links.length; i++) {
    if (linkMatchesHandle(links[i].getAttribute('href'), handle)) return true;
  }
  return false;
}

/**
 * Guards against prefix collisions — "/products/shirt" must not match
 * "shirt-blue". POD catalogs are exactly where handles share prefixes.
 */
function linkMatchesHandle(href, handle) {
  if (!href) return false;
  const marker = '/products/' + handle;
  const at = href.indexOf(marker);
  if (at === -1) return false;
  const next = href.charAt(at + marker.length);
  return next === '' || next === '?' || next === '#' || next === '/';
}

function findCardByHandle(grid, handle) {
  const cards = getCards(grid);
  for (let i = 0; i < cards.length; i++) {
    if (cardMatchesHandle(cards[i], handle)) return cards[i];
  }
  return null;
}

function findOtherCardWithHandle(grid, exclude, handle) {
  const cards = getCards(grid);
  for (let i = 0; i < cards.length; i++) {
    if (cards[i] !== exclude && cardMatchesHandle(cards[i], handle)) return cards[i];
  }
  return null;
}

// ---- scroll-animation neutralisation --------------------------------------

/**
 * Make the pinned card unconditionally visible. This is NOT cosmetic — without
 * it the feature silently ships a blank slot.
 *
 * With theme setting `animations_reveal_on_scroll` ON (Debutify's default, and
 * ON in this store's settings_data.json) every grid item renders as
 *   <li class="grid__item scroll-trigger animate--slide-in" data-cascade
 *       style="--animation-order: N">
 * and assets/base.css declares
 *   .scroll-trigger.animate--slide-in { opacity: .01 }
 *
 * Visibility is restored only by assets/animations.js, whose IntersectionObserver
 * is built ONCE at load over the elements present at that moment. Two failures
 * follow, one for each pin path:
 *
 *   MOVED CARD — a card at grid position 30 has never intersected, so it still
 *   carries `scroll-trigger--offscreen` and computes to opacity .01. Move it to
 *   slot #1 and the shopper's ad landing opens with an invisible first card.
 *   Moving a node does not re-run an IntersectionObserver callback, so nothing
 *   ever clears it.
 *
 *   INJECTED CARD — never observed at all. It would depend on
 *   `animation-delay: calc(var(--animation-order) * 75ms)` resolving, and on the
 *   slideIn keyframe's `forwards` fill landing it at opacity 1. Betting the LCP
 *   element of a paid landing page on that chain is not a bet worth taking.
 *
 * Stripping the trigger classes outright is the whole fix. facets.js reaches for
 * `scroll-trigger--cancel` in the same situation, but that class is REMOVED
 * again by onIntersection() the moment an element scrolls offscreen, so it is
 * not durable for a node we want permanently visible.
 */
function neutralizeScrollAnimation(card) {
  card.classList.remove(
    'scroll-trigger',
    'scroll-trigger--offscreen',
    'scroll-trigger--cancel',
    'scroll-trigger--design-mode',
    'animate--slide-in',
    'animate--fade-in',
    'animate--zoom-in'
  );
  card.removeAttribute('data-cascade');
  card.style.removeProperty('--animation-order');
  card.style.opacity = '';
  card.style.transform = '';
}

// ---- pinning ---------------------------------------------------------------

/**
 * Per-pin runtime state.
 *
 * `failed` latches an unresolvable handle — without it every mutation frame
 * re-issues a fetch for a handle that will never resolve.
 * `inflight` covers the round trip BEFORE `failed` or `cachedCard` can latch
 * anything: a mutation arriving while fetch #1 is still in the air (a swatch,
 * review or badge app is the usual source) would otherwise re-enter pin() and
 * fire fetch #2, #3, ... one per animation frame until the first lands.
 */
function createState() {
  return {
    pinning: false,
    cachedCard: null,
    rafId: 0,
    variantImageSrc: null,
    failed: false,
    inflight: false,
  };
}

function init(parsed, cfg) {
  const state = createState();
  pin(parsed, cfg, state);
  observeReRenders(parsed, cfg, state);
}

function pin(parsed, cfg, state) {
  // The pin retires the moment the shopper filters or sorts.
  //
  // facets.js builds its next URL from form fields only —
  //   createSearchParams(form) => new URLSearchParams(new FormData(form))
  //   updateURLHash(p)         => history.pushState(..., pathname + '?' + p)
  // so `first` is not carried over; it is wiped by any filter or sort change.
  // Re-reading the param here makes that retirement explicit and self-cleaning:
  // no event wiring, no observer teardown.
  //
  // It is also the correct product call. Without it, filtering to "Blue"
  // returns 8 products and renders 9 cards with the red one still pinned at the
  // top. And a pin that outranks "Price: low to high" is not a pin — it is a
  // broken sort. The ad intent owns the landing; the shopper's first explicit
  // choice ends it.
  if (!new URLSearchParams(window.location.search).get(cfg.param)) return;

  const grid = getGrid();
  if (!grid || state.pinning) return;

  const first = firstCardOf(grid);

  if (first && cardMatchesHandle(first, parsed.handle)) {
    // Already pinned. Infinite scroll can later append the GENUINE
    // server-rendered card for this product from its real page, which would
    // show it twice. Prefer the genuine card: swap it forward, drop ours.
    const duplicate = findOtherCardWithHandle(grid, first, parsed.handle);
    if (duplicate) {
      state.pinning = true;
      try {
        first.remove();
        neutralizeScrollAnimation(duplicate);
        moveToFront(grid, duplicate);
        prioritizePinnedImage(duplicate);
        if (parsed.variant) applyVariant(duplicate, parsed, cfg, state);
        log('replaced pinned clone with the genuine card');
      } finally {
        state.pinning = false;
      }
      return;
    }
    neutralizeScrollAnimation(first);
    if (parsed.variant) applyVariant(first, parsed, cfg, state);
    return;
  }

  state.pinning = true;
  try {
    const existing = findCardByHandle(grid, parsed.handle);

    if (existing) {
      // Already in the grid: swap slots rather than splice. See swapToFront.
      neutralizeScrollAnimation(existing);
      swapToFront(grid, existing);
      prioritizePinnedImage(existing);
      if (parsed.variant) applyVariant(existing, parsed, cfg, state);
      log('pinned an existing card', parsed.handle);
    } else if (state.cachedCard) {
      const clone = state.cachedCard.cloneNode(true);
      adoptSiblingLayout(grid, clone);
      adoptSiblingContent(grid, clone);
      neutralizeScrollAnimation(clone);
      prioritizePinnedImage(clone);
      moveToFront(grid, clone);
      if (parsed.variant) applyVariant(clone, parsed, cfg, state);
      log('re-inserted the cached fetched card');
    } else if (!state.failed && !state.inflight) {
      fetchAndPin(grid, parsed, cfg, state);
    }
  } finally {
    state.pinning = false;
  }
}

function moveToFront(grid, card) {
  const displaced = firstCardOf(grid);
  if (displaced === card) return;
  grid.insertBefore(card, displaced || grid.firstChild);
}

/**
 * Swap `card` with the grid's current first card instead of splicing it in.
 *
 * Layout-shift control for the common case. insertBefore renumbers every slot
 * between 1 and k, so the browser re-lays-out that whole run of the flow grid.
 * A swap moves exactly two boxes and leaves slots 2..k-1 untouched.
 *
 * Debutify grid items carry no positional data attributes, so unlike the
 * Horizon original there is nothing to exchange alongside the nodes.
 */
function swapToFront(grid, card) {
  const first = firstCardOf(grid);
  if (!first || first === card) return false;
  const after = card.nextSibling;
  grid.insertBefore(card, first);
  grid.insertBefore(first, after);
  return true;
}

/**
 * Copy the layout-carrying class and style attributes off a live sibling card
 * onto a fetched one.
 *
 * The fetched card comes from a section that is in no template, so its
 * `section.settings` are schema DEFAULTS, not the merchant's collection
 * settings. Where those differ — image ratio class, card style, colour scheme,
 * image shape — the clone would render subtly wrong next to its neighbours.
 *
 * Debutify makes this much lighter than Horizon did. The aspect ratio arrives
 * as a custom property scoped to `#shopify-section-{id}` (card-product.liquid
 * lines 47-69), i.e. on an ANCESTOR of the grid — so an injected card inherits
 * it for free. Only the class-carried inputs need copying.
 *
 * Copies class and style ONLY, never ids or data attributes, which are per-card
 * identity. `.card__inner` is included because it carries the `ratio` and
 * `image--ratio` classes that drive the media box.
 */
function adoptSiblingLayout(grid, card) {
  const sibling = firstCardOf(grid);
  if (!sibling || sibling === card) return;

  const selectors = ['.card', '.card__inner', '.card__media', '.media'];
  for (let i = 0; i < selectors.length; i++) {
    const from = sibling.querySelector(selectors[i]);
    const to = card.querySelector(selectors[i]);
    if (!from || !to) continue;
    const className = from.getAttribute('class');
    if (className) to.setAttribute('class', className);
    const style = from.getAttribute('style');
    if (style) to.setAttribute('style', style);
  }
}

/**
 * Optional card content, each governed by a collection-section setting rather
 * than by the product. Order does not matter; each is tested independently.
 */
const OPTIONAL_BLOCKS = [
  'card-product-variant-selects, variant-selects', // swatch row  (show_variant_picker)
  '.quick-add, .quick-add-bulk, .collecion-list-view-quick-add', // (quick_add)
  '.card__description', //                                          (show_description)
  '.card__information .caption-with-letter-spacing.light', //       (show_vendor)
  '.rating, .product-rating, [class*="card__information-rating"]', //(show_rating)
];

/**
 * Strip content blocks from a fetched card that NO real card in the grid has.
 *
 * adoptSiblingLayout() handles appearance; this handles composition. They are
 * different failure modes and neither covers the other.
 *
 * A section that sits in no template renders with its schema defaults, and it
 * has no editor UI, so those defaults are the ONLY values it will ever have.
 * Wherever a merchant's collection settings differ, the fetched card is built
 * from different arguments than its neighbours and renders a different card.
 * sections/dm-curated-card.liquid reproduces the two arguments that have theme
 * globals behind them; the other four (show_rating, show_vendor,
 * show_description, quick_add) have none, so they are corrected here instead.
 *
 * The test is "no card in the grid has this", not "the first sibling lacks it".
 * That distinction is the whole point: a product with only a default variant
 * legitimately renders no swatch row, so sampling one neighbour would delete a
 * block the section actually asked for. Absent from EVERY card means the
 * setting is off; present on some means it is a per-product difference and the
 * clone keeps it.
 *
 * Removing is safe in one direction only. If the real cards have a block the
 * clone lacks, nothing here can invent it — that needs the schema default in
 * the section file corrected for that store.
 */
function adoptSiblingContent(grid, card) {
  const siblings = getCards(grid).filter((c) => c !== card);
  if (!siblings.length) return;

  for (let i = 0; i < OPTIONAL_BLOCKS.length; i++) {
    const selector = OPTIONAL_BLOCKS[i];
    const mine = card.querySelectorAll(selector);
    if (!mine.length) continue;

    let anySiblingHasIt = false;
    for (let s = 0; s < siblings.length; s++) {
      if (siblings[s].querySelector(selector)) {
        anySiblingHasIt = true;
        break;
      }
    }
    if (anySiblingHasIt) continue;

    for (let m = 0; m < mine.length; m++) mine[m].remove();
    log('removed content block absent from every real card:', selector);
  }
}

/**
 * Mark the pinned card's image high priority.
 *
 * Every card image in Debutify is hardcoded `loading="lazy"`
 * (card-product.liquid:158, 206, 249) with no eager branch — Dawn's
 * `preload`/`image_shape` escape hatches are not wired through. The pinned card
 * is grid slot #1 on a paid landing, so it is the LCP candidate, and it arrives
 * late by construction: deferred module, plus a Section Rendering round trip on
 * the fetch path. Shipping it lazy makes the ad's own product the last image on
 * the page.
 *
 * Set before insertion on the fetch path so the request starts at high priority
 * the moment the node connects.
 */
function prioritizePinnedImage(card) {
  // Target the primary media image specifically. A bare querySelector('img')
  // is correct today but would silently retarget the moment the card gains a
  // badge or icon image above the gallery.
  const img =
    card.querySelector('.card__media img') ||
    card.querySelector('.media img') ||
    card.querySelector('img');
  if (!img) return;
  img.setAttribute('loading', 'eager');
  img.setAttribute('fetchpriority', 'high');
}

/** Fetch a standalone card through the Section Rendering API and pin it. */
function fetchAndPin(grid, parsed, cfg, state) {
  // Marked BEFORE the fetch starts, not in .then() — a mutation frame landing
  // mid-round-trip has to see this and skip re-fetching. See createState().
  state.inflight = true;

  const url =
    shopifyRoot() + 'products/' + encodeURIComponent(parsed.handle) + '?section_id=' + cfg.cardSection;

  fetch(url)
    .then((res) => {
      if (!res.ok) throw new Error('Section returned ' + res.status);
      return res.text();
    })
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const card = doc.querySelector('li.grid__item') || doc.querySelector('[data-dm-curated-pin]');
      if (!card) throw new Error('No card in rendered section');

      if (!cardMatchesHandle(card, parsed.handle)) {
        // An unknown, unpublished or draft handle still answers HTTP 200 with a
        // nil product. Caching that render made cardMatchesHandle unsatisfiable
        // forever in the original, so pin() re-inserted a fresh clone every
        // animation frame. Treat any non-matching render as a failed lookup.
        throw new Error('Rendered card does not match handle ' + parsed.handle);
      }

      // The section may carry <style>/<link> tags of its own. Its
      // `#shopify-section-dm-curated-card` scope matches nothing in this
      // document, so they are dead weight at best and a stray rule at worst.
      const junk = card.querySelectorAll('style, link[rel="stylesheet"], noscript');
      for (let i = 0; i < junk.length; i++) junk[i].remove();

      state.cachedCard = card.cloneNode(true);
      state.inflight = false;

      // Re-check: a re-render may have added the real card while we were out.
      const liveGrid = getGrid();
      if (!liveGrid || findCardByHandle(liveGrid, parsed.handle)) {
        if (liveGrid) pin(parsed, cfg, state);
        return;
      }

      state.pinning = true;
      try {
        const node = state.cachedCard.cloneNode(true);
        adoptSiblingLayout(liveGrid, node);
        adoptSiblingContent(liveGrid, node);
        neutralizeScrollAnimation(node);
        prioritizePinnedImage(node);
        moveToFront(liveGrid, node);
        if (parsed.variant) applyVariant(node, parsed, cfg, state);
        log('pinned a fetched card', parsed.handle);
      } finally {
        state.pinning = false;
      }
    })
    .catch((err) => {
      state.failed = true;
      state.inflight = false;
      if (window.console) console.warn('[dm-curated-first] could not pin product:', err.message);
    });
}

// ---- variant deep-link + image swap ---------------------------------------

function applyVariant(card, parsed, cfg, state) {
  if (!parsed.variant) return;
  setVariantOnLinks(card, parsed.handle, parsed.variant);
  if (!cfg.swapImage) return;
  if (state.variantImageSrc) {
    swapImage(card, state.variantImageSrc);
  } else {
    swapVariantImage(card, parsed.handle, parsed.variant, state);
  }
}

/**
 * Point every anchor in the card that links to THIS product at the chosen
 * variant.
 *
 * Debutify's `.card__heading a:after` overlay means the heading anchor governs
 * the whole card click, so in the default configuration one rewrite is enough.
 * We still walk all of them because quick-add, swatch pickers, carousels and
 * third-party apps each add their own, and because matching by handle leaves
 * app-injected links to OTHER products untouched.
 */
function setVariantOnLinks(card, handle, variantId) {
  const links = card.querySelectorAll('a[href*="/products/"]');
  for (let i = 0; i < links.length; i++) {
    const link = links[i];
    const href = link.getAttribute('href');
    if (!href || !linkMatchesHandle(href, handle)) continue;
    link.setAttribute('href', withVariantParam(href, variantId));
  }
}

function withVariantParam(href, variantId) {
  try {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('variant', variantId);
    return url.pathname + url.search + url.hash;
  } catch (e) {
    return href;
  }
}

/** Resolve the variant's featured image once, cache it, swap the card image. */
function swapVariantImage(card, handle, variantId, state) {
  fetch(shopifyRoot() + 'products/' + encodeURIComponent(handle) + '.js')
    .then((res) => {
      if (!res.ok) throw new Error('product.js ' + res.status);
      return res.json();
    })
    .then((product) => {
      const idNum = parseInt(variantId, 10);
      let variant = null;
      for (let i = 0; i < product.variants.length; i++) {
        if (product.variants[i].id === idNum) {
          variant = product.variants[i];
          break;
        }
      }
      if (!variant || !variant.featured_image || !variant.featured_image.src) return;
      state.variantImageSrc = variant.featured_image.src;
      swapImage(card, state.variantImageSrc);
    })
    .catch(() => {
      /* non-fatal: the card still works, it just shows the default image */
    });
}

/**
 * Swap the card's primary image only — never the hover or carousel slides,
 * which belong to <product-card-slider> and have their own state.
 *
 * srcset is rebuilt on Debutify's own width ladder (card-product.liquid:143-149)
 * so the swapped image resolves at the same breakpoints as its neighbours. The
 * native `sizes` attribute is left alone, since it is computed from
 * settings.page_width and is still correct.
 *
 * Query handling goes through the URL API rather than string surgery: an
 * existing `?v=...&other=...` src must not leave a dangling `&` after `v` is
 * stripped, nor gain a second `?` when `width` is appended.
 */
function swapImage(card, imageSrc) {
  if (card.dataset.dmVariantSwapped === imageSrc) return;

  const img =
    card.querySelector('.card__media img') ||
    card.querySelector('.media img') ||
    card.querySelector('img');
  if (!img) return;

  let base;
  try {
    base = new URL(imageSrc, window.location.href);
  } catch (e) {
    return; // not a rewritable URL — leave the existing image alone
  }
  base.searchParams.delete('v');

  const widths = [165, 360, 533, 720, 940, 1066];
  img.setAttribute('src', resizeSrc(base, 533));
  img.setAttribute('srcset', widths.map((w) => resizeSrc(base, w) + ' ' + w + 'w').join(', '));
  img.removeAttribute('data-src');
  card.dataset.dmVariantSwapped = imageSrc;
}

function resizeSrc(base, width) {
  const url = new URL(base.href);
  url.searchParams.set('width', String(width));
  return url.toString();
}

// ---- re-render survival ----------------------------------------------------

/**
 * Hold the pin through every way Debutify can rewrite the grid.
 *
 * Observed on #ProductGridContainer with subtree:true, which covers:
 *   - facets.js filter/sort  (replaces the container's entire innerHTML)
 *   - dbtfy-infinite-scroll  (insertAdjacentHTML 'beforeend' on the <ul>)
 *   - quick-add, swatches, review/badge apps rewriting individual cards
 *
 * Coalesced to one pin() per animation frame via rafId. Even when already
 * pinned, pin() re-scans once — that is what catches an infinite-scroll
 * duplicate, and what wins the race against a swatch app rewriting the card
 * link back to its default variant. O(cards) per frame, not per mutation.
 */
function observeReRenders(parsed, cfg, state) {
  const container = getContainer();
  if (!container || typeof MutationObserver === 'undefined') return;

  const observer = new MutationObserver(() => {
    if (state.pinning || state.rafId) return;
    state.rafId = window.requestAnimationFrame(() => {
      state.rafId = 0;
      pin(parsed, cfg, state);
    });
  });

  observer.observe(container, { childList: true, subtree: true });
}
